import { useCallback, useEffect, useRef, useState } from "react";
import { byId, byKey, durationOf, tourAnimations } from "../animations/registry";
import { partyGroup } from "../animations/party";
import { listenForUnlock, stopSamples } from "../audio/audioEngine";
import { playCue } from "../audio/cues";
import { warmSamples } from "../audio/samples";
import { watchFrames } from "../utils/renderTier";

// De motor achter de animaties: hij houdt bij wat er nú speelt en ruimt het
// vanzelf weer op. Meerdere animaties tegelijk mag - een verkoop is een
// balk én confetti én een oplichtende rij.
//
// Elke run krijgt een eigen runId, zodat dezelfde animatie twee keer kort na
// elkaar echt opnieuw begint in plaats van door te lopen (React hergebruikt
// anders hetzelfde element en de CSS-animatie herstart niet).
//
// Het geluid hangt aan dezelfde play(): elke animatie die begint speelt zijn
// geluid (audio/cues.js), en animaties zonder geluid zijn stil. Zo klinkt het
// animatielab precies zoals de tv, want dat gaat langs dezelfde weg.
//
// Eén verkoop is zo'n groepje animaties, en die groepjes staan in de rij:
// komt er een tweede verkoop binnen terwijl de eerste nog loopt, dan begint
// die pas als álles van de eerste voorbij is. Twee verkopen door elkaar heen
// is op een tv niet te volgen, en één verkoop is al gauw een kwart minuut.
// play() zelf gaat wél direct: dat is de toets en het animatielab, waar je
// juist meteen wil zien wat je aanwijst.

let counter = 0;

// Hoeveel er tussen twee animaties van hetzelfde groepje zit.
const stagger = 700;

// En hoeveel stilte er tussen twee verkopen zit. Zonder deze pauze valt de
// balk van de volgende verkoop op hetzelfde moment naar beneden als die van
// de vorige omhoog gaat, en dan lijkt het één doorlopend geheel in plaats
// van twee verkopen.
const groupPause = 3500;

// Zoveel verkopen mogen er wachten. Meer dan dit en het dashboard loopt
// minuten achter op de werkelijkheid, dus dan laten we de nieuwste vallen:
// de stand op de kaarten is dan alsnog bij, alleen het feestje niet.
const queueMax = 6;

// Licht de rij of podiumtrede van één verkoper op. Die staan verspreid over
// drie kaarten, dus we zoeken ze op via data-name in plaats van de klasse
// door de hele componentenboom door te geven.
function highlightSeller(name, ms) {
  if (!name) return () => {};

  let nodes = [];
  // Eén frame wachten: bij een verkoop komen de nieuwe cijfers en de animatie
  // tegelijk binnen, en dan staat de ranglijst pas ná dat frame op de nieuwe
  // volgorde. Zoeken we eerder, dan lichten we de verkeerde rij op.
  const frame = requestAnimationFrame(() => {
    nodes = Array.from(
      document.querySelectorAll(`[data-name="${CSS.escape(name)}"]`),
    );
    nodes.forEach((node) => node.classList.add("fx-highlight"));
  });

  const clean = () => nodes.forEach((node) => node.classList.remove("fx-highlight"));
  const timer = setTimeout(clean, ms);

  return () => {
    cancelAnimationFrame(frame);
    clearTimeout(timer);
    clean();
  };
}

// De toets waarmee het feestje aangaat: Shift+8. Op de toets zoals hij op dit
// toetsenbord staat en niet op event.code, want daar staat Shift niet in.
const partyKey = "*";

export function useAnimations({ keyboard = true, party = true } = {}) {
  const [active, setActive] = useState([]);
  const timers = useRef(new Map());

  // De wachtrij: groepjes die nog moeten, de timers van het groepje dat nu
  // loopt, en of er iets loopt. In refs, want de wachtrij hoort niet opnieuw
  // te tekenen - hij bepaalt alleen wannéér er iets gebeurt.
  const queue = useRef([]);
  const groupTimers = useRef([]);
  const busy = useRef(false);

  const clear = useCallback((runId) => {
    const timer = timers.current.get(runId);
    if (timer) {
      clearTimeout(timer.id);
      timer.cleanup?.();
      timers.current.delete(runId);
    }
    setActive((list) => list.filter((run) => run.runId !== runId));
  }, []);

  const play = useCallback(
    (id, payload = {}) => {
      const animation = byId(id);
      if (!animation) return;

      const runId = ++counter;
      const ms = durationOf(animation, payload);

      // Beeld en geluid beginnen samen; de engine kijkt zelf of er geluid
      // uit mag komen, dus hier hoeft niets afgevangen te worden. quiet: het
      // beeld zonder zijn eigen geluid, omdat er al iets anders klinkt. Zie
      // targetPartyGroup in animations/party.js.
      if (!payload.quiet) playCue(id);

      const cleanup =
        animation.target === "seller"
          ? highlightSeller(payload.name, ms)
          : null;

      timers.current.set(runId, {
        id: setTimeout(() => clear(runId), ms),
        cleanup,
      });
      setActive((list) => [...list, { runId, id, payload }]);
      return runId;
    },
    [clear],
  );

  const stopAll = useCallback(() => {
    // Ook het geluid. De tonen die het dashboard zelf maakt duren een fractie
    // van een seconde en zijn hier al voorbij, maar een opname loopt door: het
    // feestje is een kwart minuut, en dat hoort op te houden als het beeld
    // ophoudt. Zie stopSamples in audio/audioEngine.js.
    stopSamples();

    timers.current.forEach((timer) => {
      clearTimeout(timer.id);
      timer.cleanup?.();
    });
    timers.current.clear();

    groupTimers.current.forEach(clearTimeout);
    groupTimers.current = [];
    queue.current = [];
    busy.current = false;

    setActive([]);
  }, []);

  // play() en runGroup verwijzen naar elkaar - een groepje speelt animaties,
  // en het eind van een groepje start het volgende - dus via een ref in
  // plaats van via de afhankelijkheden van useCallback.
  const self = useRef({});
  self.current.play = play;

  // Eén groepje afspelen, en als de laatste animatie is uitgelopen is de
  // volgende verkoop aan de beurt.
  //
  // Elke regel is [animatie, gegevens] en mag als derde een tijd hebben: dat
  // is hoeveel ms na het begin van het groepje hij start. Zonder tijd komt
  // hij `stagger` na de vorige. Zo bepaalt een verkoop zijn eigen volgorde
  // (eerst de aankondiging, dan de balk, dan de regen; zie saleGroup in
  // useSaleEvents) en speelt een los groepje gewoon kort na elkaar.
  const runGroup = useCallback((events) => {
    busy.current = true;
    groupTimers.current = [];

    let total = 0;
    let previous = -stagger;
    events.forEach(([id, payload, at]) => {
      const start = at ?? previous + stagger;
      previous = start;
      const animation = byId(id);
      total = Math.max(total, start + (animation ? durationOf(animation, payload) : 0));
      groupTimers.current.push(
        setTimeout(() => self.current.play(id, payload), start),
      );
    });

    // De pauze zit binnen de wachttijd, dus blijft busy tot na de stilte
    // staan: een verkoop die er tijdens de pauze bij komt wacht die netjes
    // uit in plaats van er middenin te beginnen.
    groupTimers.current.push(
      setTimeout(() => {
        busy.current = false;
        const next = queue.current.shift();
        if (next) self.current.runGroup(next);
      }, total + groupPause),
    );
  }, []);
  self.current.runGroup = runGroup;

  // Wat er bij één verkoop hoort, als lijst van [animatie, gegevens]. Loopt
  // er nog iets, dan gaat dit groepje in de rij.
  //
  // keep: ook in de rij als die vol zit. Voor het inhalen na een herstart
  // (zie useSaleEvents): daar is achterlopen juist de bedoeling, en een
  // verkoop van gisteravond laten vallen omdat er al zes wachten zou het
  // hele inhalen zinloos maken.
  const playGroup = useCallback(
    (events, { keep = false } = {}) => {
      if (!events?.length) return;

      if (busy.current) {
        if (keep || queue.current.length < queueMax) queue.current.push(events);
        return;
      }

      runGroup(events);
    },
    [runGroup],
  );

  // Het feestje: Shift+8, of "feest" intypen op de tv. Eerst alles stoppen en
  // dan meteen beginnen: je zet dit aan omdat je het nú wil zien, niet achter
  // een verkoop aan die nog in de rij staat. Wat er speelt staat in
  // animations/party.js.
  const playParty = useCallback(() => {
    stopAll();
    playGroup(partyGroup());
  }, [playGroup, stopAll]);

  // Alles achter elkaar, met een korte pauze ertussen. Handig om in één keer
  // te zien wat er allemaal is.
  const playAll = useCallback(() => {
    stopAll();
    let wait = 0;
    tourAnimations.forEach((animation) => {
      // Alleen hier de voorbeeldgegevens: dit is de rondleiding, niet iets
      // wat uit echte cijfers komt.
      setTimeout(() => play(animation.id, animation.demo || {}), wait);
      wait += Math.min(animation.ms, 3000) + 400;
    });
  }, [play, stopAll]);

  // Shift+8 luistert altijd, ook als de cijfertoetsen uit staan. Die staan op
  // de tv uit omdat niemand daar een verkoop hoort te kunnen verzinnen (zie
  // testShortcuts in constants.js), en dat is hier niet aan de hand: het
  // feestje verzint niets, het viert alleen. Zonder dit zou het op de tv
  // alleen via de verborgen code bereikbaar zijn, en juist daar wil je er met
  // één toets bij kunnen.
  useEffect(() => {
    if (!party) return undefined;

    const onKey = (event) => {
      const tag = event.target?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || event.target?.isContentEditable) return;
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      if (event.key === partyKey) playParty();
    };

    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [party, playParty]);

  useEffect(() => {
    if (!keyboard) return undefined;

    const onKey = (event) => {
      // Niet meepikken terwijl iemand in een veld typt.
      const tag = event.target?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || event.target?.isContentEditable) return;
      if (event.metaKey || event.ctrlKey || event.altKey) return;

      if (event.key === "Escape") return stopAll();
      if (event.key === " ") {
        event.preventDefault();
        return playAll();
      }

      // Met de voorbeeldgegevens erbij: een toets is om te kijken hoe het
      // eruitziet, en dan hoort er ook te staan wát er verkocht is.
      const animation = byKey(event.key);
      if (animation) play(animation.id, animation.demo || {});
    };

    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [keyboard, play, playAll, stopAll]);

  // Bij het verlaten van het scherm alle timers opruimen.
  useEffect(() => () => stopAll(), [stopAll]);

  // Geluid mag pas nadat er iets is aangeraakt; op de tv gebeurt dat nooit,
  // dus daar doet de kioskvlag van Chromium het werk. Zie audio/audioEngine.js.
  // Eigen geluidsbestanden worden meteen opgehaald: bij de eerste verkoop is
  // daar geen tijd meer voor, en dan zou je hem die keer nog niet horen.
  useEffect(() => {
    warmSamples();
    return listenForUnlock();
  }, []);

  // Terwijl er iets beweegt: kijken of het apparaat het bijhoudt. Zo niet,
  // dan zakt de kwaliteitstrap en is de volgende animatie lichter. Dit is
  // het eerlijkste moment om te meten, want dit is precies het beeld waar
  // het om gaat. Zie utils/renderTier.js.
  const busyNow = active.length > 0;
  useEffect(() => {
    if (!busyNow) return undefined;
    return watchFrames();
  }, [busyNow]);

  // Animaties die een bestaand onderdeel aansturen (de meter, het podium)
  // doen dat via een klasse op de buitenste div.
  const rootClass = active.map((run) => `anim-${run.id}`).join(" ");

  return { active, play, playGroup, playParty, playAll, stopAll, rootClass };
}
